/**
 * Frontend-only signup state. Persisted in sessionStorage so a refresh
 * mid-flow doesn't lose progress. Backend wiring (Supabase upserts after
 * each screen) replaces these helpers later.
 */

import { migrateCommunityIsPrivate } from '../../lib/communityPrivacy'

const STORAGE_KEY = 'statbox.signupDraft.v1'

/**
 * Community logo captured during signup (base64 data URL).
 * Written from /signup/community (and mirrored to the legacy key below
 * so SetupCompletingTransition keeps reading the logo from its existing
 * key without code changes).
 */
export const STATBOX_LOGO_DATA_URL_KEY = 'statbox_logo_data_url'
const STATBOX_LOGO_DATA_URL_LEGACY_KEY = 'statbox.community.logo.v1'

export function readSignupCommunityLogoDataUrl() {
  try {
    let v = localStorage.getItem(STATBOX_LOGO_DATA_URL_KEY) || ''
    if (!v) {
      v = localStorage.getItem(STATBOX_LOGO_DATA_URL_LEGACY_KEY) || ''
      if (v) {
        try {
          localStorage.setItem(STATBOX_LOGO_DATA_URL_KEY, v)
        } catch {
          /* noop */
        }
      }
    }
    return v
  } catch {
    return ''
  }
}

export function writeSignupCommunityLogoDataUrl(dataUrl) {
  try {
    if (dataUrl) {
      localStorage.setItem(STATBOX_LOGO_DATA_URL_KEY, dataUrl)
      localStorage.setItem(STATBOX_LOGO_DATA_URL_LEGACY_KEY, dataUrl)
    } else {
      localStorage.removeItem(STATBOX_LOGO_DATA_URL_KEY)
      localStorage.removeItem(STATBOX_LOGO_DATA_URL_LEGACY_KEY)
    }
  } catch {
    /* QuotaExceeded or disabled storage */
  }
}

// Order of steps in the post-signup setup wizard. Used by the wizard's
// chrome to compute prev/next navigation, and by the welcome page's
// resume banner to compute the next-incomplete step.
//
// 'policies' is the final step (visibility + WhatsApp contact). The
// previous 'branding' step (logo upload + accent picker) was retired —
// logo capture moved to /signup/community, accent customization was
// dropped entirely. The standalone 'invite' step was retired (sharing
// is on welcome + portal roster). The old 'schedule' step
// (playDays/playTimes) was also previously removed.
//
// markStepCompleted/markStepSkipped filter against this list so stale
// 'branding' / 'invite' / 'schedule' entries in completedSteps /
// skippedSteps from older drafts get dropped on the next state
// transition. No explicit migration needed.
export const WIZARD_STEPS_ORDER = ['locations', 'size', 'policies']

const EMPTY_POLICIES = {
  isPrivate: false,
  // Legacy — migrated to isPrivate on read; no longer written.
  joinPolicy: null,
  visibility: null,
  // Diagnostic answer for the policies step. Drives which contact /
  // integration fields render below the privacy cards. Switching
  // values keeps the inactive branch's draft fields intact, so the
  // user can flip back without losing data.
  managementType: null,           // 'whatsapp' | 'app' | null
  // Captured when managementType === 'whatsapp':
  whatsappNumber: null,           // raw user-entered digits/spaces/dashes
  whatsappNumberCountryCode: '',  // ISO-2; defaults to draft.country at write-time
  whatsappGroupLink: null,        // freeform string; soft-validated in UI
  // Captured when managementType === 'app':
  currentPlatform: null,          // platform key from MANAGEMENT_PLATFORMS
  currentPlatformOther: null,     // freeform string when currentPlatform === 'other'
  platformHandle: null,           // optional URL or handle into the platform
  integrationsInterest: true,     // checkbox; defaults to opt-in
}

const EMPTY_SETUP_WIZARD = {
  completedSteps: [],          // subset of WIZARD_STEPS_ORDER, user-confirmed
  skippedSteps: [],            // subset of WIZARD_STEPS_ORDER, user-skipped
  // Structured venue records sourced from the Places-style autocomplete.
  // Manually-added venues (no Google match) carry isManual: true and
  // null-ish values for the fields we couldn't infer (lat/lng/photoUrl/
  // rating/ratingCount). instagramHandle is user-supplied and optional.
  venues: [],                  // see migrateVenues() for the per-item shape
  communitySize: '',           // '' | 'under_200' | '200_500' | '500_1000' | '1000_plus'
  policies: { ...EMPTY_POLICIES },
}

// Old drafts persisted venues as { name: string }. The new schema is
// fully structured (placeId, lat/lng, instagramHandle, isManual, ...).
// Rather than upgrade in place — there are no production users yet — we
// drop legacy entries on read so downstream code can assume the new
// shape unconditionally.
function migrateVenues(rawVenues) {
  if (!Array.isArray(rawVenues)) return []
  return rawVenues.filter(
    (v) => v && typeof v === 'object' && typeof v.placeId === 'string',
  )
}

// Old size buckets (Under 50 / 50-200 / 200-500 / 500+) shifted to a new
// distribution (Under 200 / 200-500 / 500-1000 / 1000+). The buckets
// don't map cleanly except at the top: someone who picked "500+" still
// belongs in the new top bucket "1000+", so we preserve their intent.
// Everyone else gets reset rather than silently misclassified.
//
// We accept both dash and underscore forms because legacy drafts used
// dashes (`'under-50'`) but the new enum uses underscores. Any value
// outside the new whitelist (or the explicit top-bucket alias) is
// dropped.
const VALID_COMMUNITY_SIZES = new Set([
  'under_200',
  '200_500',
  '500_1000',
  '1000_plus',
])
function migrateCommunitySize(raw) {
  if (!raw || typeof raw !== 'string') return ''
  if (VALID_COMMUNITY_SIZES.has(raw)) return raw
  if (raw === '500-plus' || raw === '500_plus') return '1000_plus'
  return ''
}

// Academy persona intake — captured on /signup/academy/book. All
// three intake fields are optional (the user can book a Calendly
// without filling them). bookedAt is set automatically when the
// embedded Calendly widget fires its event_scheduled postMessage.
const EMPTY_ACADEMY = {
  name: '',                       // freeform academy name
  country: '',                    // ISO-2 code, mirrors top-level `country` semantics
  size: '',                       // '' | 'under_100' | '100_300' | '300_700' | '700_plus'
  bookedAt: '',                   // ISO string, set when Calendly booking completes
  bookedByUserId: '',             // auth user_id that completed the booking
}

// Community persona intake — captured on /signup/community (Calendly
// booking, same pattern as academy). bookedAt is set when the user
// completes an activation call booking.
const EMPTY_COMMUNITY = {
  name: '',                       // freeform community name
  size: '',                       // '' | 'under_200' | '200_500' | '500_1000' | '1000_plus'
  bookedAt: '',                   // ISO string, set when Calendly booking completes
  bookedByUserId: '',             // auth user_id that completed the booking
}

// Venue persona intake — captured on /signup/venue/book (Calendly
// booking, same pattern as academy). bookedAt is set when the user
// completes a demo call booking. Legacy wizard fields (address,
// photos, courts) remain on the draft shape for older sessions.
const EMPTY_VENUE_ADDRESS = {
  placeId: null,
  formattedAddress: null,
  lat: null,
  lng: null,
  name: null,                     // Google Places name (used to pre-fill venue.name)
  countryCode: null,              // ISO-2, when Places returns it
  photoUrl: null,                 // primary photo, used in the confirmation card
}

const EMPTY_VENUE = {
  name: '',                       // freeform venue name
  country: '',                    // ISO-2; mirrors top-level country semantics
  address: { ...EMPTY_VENUE_ADDRESS },
  sportType: '',                  // '' | 'football' | 'padel' | 'basketball' | 'multi_sport'
  courtRange: '',                 // '' | '1_2' | '3_5' | '6_10' | '10_plus'
  photos: [],                     // array of data URLs (max 5)
  courts: [],                     // [{ id, name, type, environment, size }]
  bookedAt: '',                   // ISO string, set when Calendly booking completes
  bookedByUserId: '',             // auth user_id that completed the booking
}

// Persisted replay_venues.id once the signup wizard is saved to Supabase.
// Lives on the draft root (like communityId), not inside venue.*.

/**
 * Migrate a courts array from older drafts (which lacked the
 * environment + size fields) to the current schema. Defensive only —
 * we don't try to guess what the user meant; environment defaults to
 * null so the "missing setting" microcopy in the Courts step gently
 * surfaces incomplete rows. size defaults to null so the conditional
 * select doesn't render with stale state.
 */
function migrateCourts(courts) {
  if (!Array.isArray(courts)) return []
  return courts
    .filter((c) => c && typeof c === 'object' && typeof c.id === 'string')
    .map((c) => ({
      id: c.id,
      name: typeof c.name === 'string' ? c.name : '',
      type: typeof c.type === 'string' ? c.type : 'other',
      environment:
        c.environment === 'indoor' || c.environment === 'outdoor'
          ? c.environment
          : null,
      size: typeof c.size === 'string' && c.size.length > 0 ? c.size : null,
    }))
}

/**
 * Venue photos captured during step 1 (data URL array).
 * Persisted to localStorage so a refresh mid-flow doesn't lose them.
 * Spec caps at 5 entries; the writer trims defensively.
 */
const STATBOX_VENUE_PHOTOS_KEY = 'statbox_venue_photos'
const VENUE_PHOTOS_MAX = 5

export function readSignupVenuePhotos() {
  try {
    const raw = localStorage.getItem(STATBOX_VENUE_PHOTOS_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.filter((s) => typeof s === 'string' && s.startsWith('data:'))
  } catch {
    return []
  }
}

export function writeSignupVenuePhotos(photos) {
  try {
    if (!Array.isArray(photos) || photos.length === 0) {
      localStorage.removeItem(STATBOX_VENUE_PHOTOS_KEY)
      return
    }
    const trimmed = photos
      .filter((s) => typeof s === 'string' && s.startsWith('data:'))
      .slice(0, VENUE_PHOTOS_MAX)
    localStorage.setItem(STATBOX_VENUE_PHOTOS_KEY, JSON.stringify(trimmed))
  } catch {
    /* QuotaExceeded or disabled storage */
  }
}

const EMPTY_DRAFT = {
  // Screen 0
  authMethod: null,        // 'google' | 'microsoft'
  email: '',
  // Screen 1
  persona: null,           // 'community' | 'academy' | 'venue'
  // Screen 2
  fullName: '',
  phone: '',               // E.164 or raw digits from activation-call booking
  phoneCountryCode: '',    // e.g. '+961'
  phoneNumber: '',         // digits without country code
  organizationName: '',
  country: '',             // ISO-2 code
  city: '',
  primarySports: [],       // subset of ['football','basketball','padel']
  // Screen 3
  onboardingData: {},      // persona-specific answers
  // Post-signup setup wizard (4 optional steps before the welcome screen)
  setupWizard: { ...EMPTY_SETUP_WIZARD },
  // Academy persona intake (mirror of community's screen-3 fields,
  // captured on /signup/academy/book before the Calendly embed).
  academy: { ...EMPTY_ACADEMY },
  // Community persona intake (Calendly booking on /signup/community).
  community: { ...EMPTY_COMMUNITY },
  // Venue persona intake (captured on the 2-step
  // /signup/venue/book Calendly intake).
  venue: { ...EMPTY_VENUE },
  // Activation lifecycle (setup mode)
  // BACKEND TODO: replace with real org fields sourced from Calendly
  // webhooks + post-call status updates.
  activationCallBooked: false,
  activationCallBookedAt: '',    // ISO string
  activationCallCompletedAt: '', // ISO string
  // Lifecycle
  signupStep: 0,           // furthest step reached
  accountStatus: 'incomplete',
  venueId: null,           // replay_venues.id after wizard persist
}

export function readDraft() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...EMPTY_DRAFT }
    const parsed = JSON.parse(raw)
    // Deep-merge setupWizard so existing drafts (without it) get the
    // default schema rather than `undefined` accesses downstream. Run
    // venues through the migrator so old string-style entries are
    // dropped before they reach the new UI.
    const mergedWizard = {
      ...EMPTY_SETUP_WIZARD,
      ...(parsed.setupWizard || {}),
    }
    mergedWizard.venues = migrateVenues(mergedWizard.venues)
    mergedWizard.communitySize = migrateCommunitySize(mergedWizard.communitySize)
    // Shallow-merge policies so partial saves from /signup/setup/policies
    // (and any pre-policies drafts that lack the field entirely) always
    // expose the full shape downstream.
    mergedWizard.policies = {
      ...EMPTY_POLICIES,
      ...(mergedWizard.policies || {}),
    }
    mergedWizard.policies.isPrivate = migrateCommunityIsPrivate(mergedWizard.policies)
    // Same defensive shape merge for academy. Older drafts predate the
    // field; new drafts may have only partial keys (e.g. only bookedAt
    // if the user booked without filling the form).
    const mergedAcademy = {
      ...EMPTY_ACADEMY,
      ...(parsed.academy || {}),
    }
    const mergedCommunity = {
      ...EMPTY_COMMUNITY,
      ...(parsed.community || {}),
    }
    // Venue gets the same defensive treatment, plus a nested address
    // merge so partial Places picks (manual entry without lat/lng)
    // still surface the full address shape downstream.
    const incomingVenue = parsed.venue || {}
    const mergedVenue = {
      ...EMPTY_VENUE,
      ...incomingVenue,
      address: {
        ...EMPTY_VENUE_ADDRESS,
        ...(incomingVenue.address || {}),
      },
      photos: Array.isArray(incomingVenue.photos) ? incomingVenue.photos : [],
      courts: migrateCourts(incomingVenue.courts),
    }
    return {
      ...EMPTY_DRAFT,
      ...parsed,
      setupWizard: mergedWizard,
      academy: mergedAcademy,
      community: mergedCommunity,
      venue: mergedVenue,
    }
  } catch {
    return { ...EMPTY_DRAFT }
  }
}

// Broadcasts every draft mutation as a window event so same-tab
// consumers can react to changes without polling sessionStorage on
// every render. The native `storage` event only fires for OTHER
// tabs, which means components like the wizard identity strip
// would otherwise stay stale during step 1 (where draft.venue.name
// is being typed) until the next route change forced a re-render.
const SIGNUP_DRAFT_EVENT = 'signup-draft-updated'

export function writeDraft(patch) {
  try {
    const next = { ...readDraft(), ...patch }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SIGNUP_DRAFT_EVENT))
      }
    } catch { /* dispatch failure is non-fatal */ }
    return next
  } catch {
    return readDraft()
  }
}

/**
 * Subscribe to draft updates in the SAME tab. Returns the
 * unsubscribe function. Use `useEffect` to subscribe at mount and
 * the returned function to clean up. Bump a tick state from the
 * listener to force a re-render where you want fresh draft reads.
 */
export function subscribeSignupDraft(listener) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(SIGNUP_DRAFT_EVENT, listener)
  return () => window.removeEventListener(SIGNUP_DRAFT_EVENT, listener)
}

export function clearDraft() {
  try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* noop */ }
}

/** Drop academy call booking flags (e.g. on sign-out so re-login shows the scheduler). */
export function clearAcademySignupBooking() {
  const draft = readDraft()
  if (!draft.academy?.bookedAt && !draft.academy?.bookedByUserId) return
  writeDraft({
    academy: {
      ...EMPTY_ACADEMY,
      ...draft.academy,
      bookedAt: '',
      bookedByUserId: '',
    },
  })
}

/** Drop venue call booking flags (e.g. on sign-out so re-login shows the scheduler). */
export function clearVenueSignupBooking() {
  const draft = readDraft()
  if (!draft.venue?.bookedAt && !draft.venue?.bookedByUserId) return
  writeDraft({
    venue: {
      ...EMPTY_VENUE,
      ...draft.venue,
      bookedAt: '',
      bookedByUserId: '',
    },
  })
}

/** Drop community call booking flags (e.g. on sign-out so re-login shows the scheduler). */
export function clearCommunitySignupBooking() {
  const draft = readDraft()
  if (!draft.community?.bookedAt && !draft.community?.bookedByUserId) return
  writeDraft({
    community: {
      ...EMPTY_COMMUNITY,
      ...draft.community,
      bookedAt: '',
      bookedByUserId: '',
    },
  })
}

/**
 * bookedAt from the draft only when it belongs to the signed-in user.
 * Stale or anonymous entries are cleared.
 */
export function resolveAcademyBookedAtForUser(userId) {
  const draft = readDraft()
  const bookedAt = draft.academy?.bookedAt || ''
  if (!bookedAt || !userId) {
    if (bookedAt && !userId) clearAcademySignupBooking()
    return ''
  }
  const bookedBy = draft.academy?.bookedByUserId || ''
  if (!bookedBy || bookedBy !== userId) {
    clearAcademySignupBooking()
    return ''
  }
  return bookedAt
}

/**
 * bookedAt from the draft only when it belongs to the signed-in user.
 * Stale or anonymous entries are cleared.
 */
export function resolveVenueBookedAtForUser(userId) {
  const draft = readDraft()
  const bookedAt = draft.venue?.bookedAt || ''
  if (!bookedAt || !userId) {
    if (bookedAt && !userId) clearVenueSignupBooking()
    return ''
  }
  const bookedBy = draft.venue?.bookedByUserId || ''
  if (!bookedBy || bookedBy !== userId) {
    clearVenueSignupBooking()
    return ''
  }
  return bookedAt
}

/**
 * bookedAt from the draft only when it belongs to the signed-in user.
 * Stale or anonymous entries are cleared.
 */
export function resolveCommunityBookedAtForUser(userId) {
  const draft = readDraft()
  const bookedAt = draft.community?.bookedAt || ''
  if (!bookedAt || !userId) {
    if (bookedAt && !userId) clearCommunitySignupBooking()
    return ''
  }
  const bookedBy = draft.community?.bookedByUserId || ''
  if (!bookedBy || bookedBy !== userId) {
    clearCommunitySignupBooking()
    return ''
  }
  return bookedAt
}

let signupAuthCleanupRegistered = false

/** Clear signup booking flags whenever Supabase session ends. */
export function registerSignupAuthCleanup(supabaseClient) {
  if (signupAuthCleanupRegistered || typeof window === 'undefined') return
  signupAuthCleanupRegistered = true
  supabaseClient.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      clearAcademySignupBooking()
      clearVenueSignupBooking()
      clearCommunitySignupBooking()
    }
  })
}

/**
 * Maps signup_step -> next route. Used by SignupCallback (and Screen 0)
 * to resume the user where they left off.
 */
/** Intake step for an authenticated user finishing org setup (Try Statbox funnel). */
export function signupIntakePath(persona) {
  if (persona === 'community') return '/upload'
  if (persona === 'academy') return '/signup/academy/book'
  if (persona === 'venue') return '/signup/venue/book'
  return '/signup'
}

export function beginSignupIntake(persona) {
  const draft = readDraft()
  writeDraft({
    ...draft,
    persona: persona || draft.persona || null,
    signupStep: Math.max(Number(draft.signupStep) || 0, 2),
  })
  return signupIntakePath(persona)
}

export function routeForStep(step, accountStatus = 'incomplete', persona = null) {
  if (accountStatus === 'activated') return '/'
  if (accountStatus === 'setup_mode') {
    if (persona === 'community') return '/community/dashboard'
    if (persona === 'academy') return '/academy/welcome'
    if (persona === 'venue') return '/venue-owner/welcome'
    return '/'
  }

  const s = Number(step ?? 0)
  if (s >= 2) {
    if (persona === 'community') return '/upload'
    if (persona === 'academy') return '/signup/academy/book'
    if (persona === 'venue') return '/signup/venue/book'
    return '/signup'
  }
  if (s >= 1) return '/signup/account'
  return '/signup'
}

/* -------------------- Setup-wizard helpers -------------------- */

function patchSetupWizard(patch) {
  const draft = readDraft()
  const wizard = draft.setupWizard || EMPTY_SETUP_WIZARD
  return writeDraft({ setupWizard: { ...wizard, ...patch } })
}

/** Mark a wizard step as completed (and remove it from skipped if present). */
export function markStepCompleted(stepName) {
  if (!WIZARD_STEPS_ORDER.includes(stepName)) return
  const wizard = readDraft().setupWizard
  const completed = new Set(wizard.completedSteps || [])
  const skipped = new Set(wizard.skippedSteps || [])
  completed.add(stepName)
  skipped.delete(stepName)
  patchSetupWizard({
    completedSteps: WIZARD_STEPS_ORDER.filter((s) => completed.has(s)),
    skippedSteps: WIZARD_STEPS_ORDER.filter((s) => skipped.has(s)),
  })
}

/** Mark a wizard step as skipped (and remove it from completed if present). */
export function markStepSkipped(stepName) {
  if (!WIZARD_STEPS_ORDER.includes(stepName)) return
  const wizard = readDraft().setupWizard
  const completed = new Set(wizard.completedSteps || [])
  const skipped = new Set(wizard.skippedSteps || [])
  skipped.add(stepName)
  completed.delete(stepName)
  patchSetupWizard({
    completedSteps: WIZARD_STEPS_ORDER.filter((s) => completed.has(s)),
    skippedSteps: WIZARD_STEPS_ORDER.filter((s) => skipped.has(s)),
  })
}

export function isStepCompleted(stepName) {
  const draft = readDraft()
  return (draft.setupWizard?.completedSteps || []).includes(stepName)
}

/**
 * Returns the slug of the next step the user hasn't acted on (neither
 * completed nor skipped). Returns null if every step has been accounted
 * for. Used by the welcome page's resume banner.
 */
export function getNextIncompleteStep() {
  const wizard = readDraft().setupWizard || EMPTY_SETUP_WIZARD
  const completed = new Set(wizard.completedSteps || [])
  const skipped = new Set(wizard.skippedSteps || [])
  for (const step of WIZARD_STEPS_ORDER) {
    if (!completed.has(step) && !skipped.has(step)) return step
  }
  return null
}
